<template>
  <ul ref="sortableRootRef" :role="level === 0 ? 'tree' : 'group'">
    <li v-if="anyItemHasInnerControls" role="presentation" class="side-menu-v3__a11y-hints">
      <zm-visually-hidden :id="hintHandlePinId">{{ HINT_HANDLE_PIN_TEXT }}</zm-visually-hidden>
      <zm-visually-hidden :id="hintHandleId">{{ HINT_HANDLE_TEXT }}</zm-visually-hidden>
      <zm-visually-hidden :id="hintPinId">{{ HINT_PIN_TEXT }}</zm-visually-hidden>
    </li>
    <li
      v-for="(processedItem, index) in items"
      :key="getItemProp(processedItem, 'id')"
      :id="`${isPinnedMenu ? 'pinned-menu' : 'menu'}-${getItemProp(processedItem, 'id')}`"
      :class="[
        'side-menu-v3__item',
        sortable &&
          !isItemTitle(processedItem) &&
          !isItemSeparator(processedItem) &&
          'is-sortable-draggable'
      ]"
      role="none"
      @focusin="handleRowFocusIn($event, processedItem)"
      @focusout="handleRowFocusOut($event, processedItem)"
      @contextmenu="handleItemContextMenu($event, processedItem)">
      <div
        v-if="isItemTitle(processedItem)"
        class="side-menu-v3__item-content is-title"
        role="none">
        <span class="side-menu-v3__item-title">{{ getItemLabel(processedItem) }}</span>
      </div>
      <component
        :is="getItemProp(processedItem, 'url') ? 'a' : 'div'"
        v-else
        :id="getItemProp(processedItem, 'id')"
        :tabindex="getTabIndex(processedItem, index)"
        role="treeitem"
        :href="getItemProp(processedItem, 'url')"
        :target="getItemProp(processedItem, 'target')"
        :class="[
          'side-menu-v3__item-content',
          isItemActive(processedItem) && 'is-active',
          isItemSeparator(processedItem) && 'is-separator',
          isItemTitle(processedItem) && 'is-title',
          getItemProp(processedItem, 'muted') && 'is-muted',
          isItemDisabled(processedItem) && 'is-disabled'
        ]"
        :aria-hidden="isItemSeparator(processedItem) || undefined"
        :aria-current="isItemActive(processedItem) ? 'page' : undefined"
        :aria-owns="isItemGroup(processedItem) ? getItemId(processedItem) + '_list' : undefined"
        :aria-expanded="isItemGroup(processedItem) ? processedItem.expanded : undefined"
        :aria-describedby="getDescribedById(processedItem)"
        :aria-disabled="isItemDisabled(processedItem) || undefined"
        :aria-label="getItemAriaLabel(processedItem)"
        @pointerenter="handleItemMouseenter($event, processedItem)"
        @pointerleave="handleItemMouseleave($event, processedItem)"
        @touchstart="handleItemTouchStart"
        @touchend="handleItemTouchEnd($event, processedItem)"
        @click.stop="handleItemClick($event, processedItem)"
        @keydown="handleKeydown($event, processedItem)">
        <template
          v-if="
            !isInHoverPopup &&
            getItemIcon(processedItem) &&
            (level === 0 || processedItem.item._pinned)
          ">
          <span
            v-if="isPinnedHandle(processedItem)"
            class="side-menu-v3__item-handle"
            :class="{
              'is-grabbed': isItemGrabbed(processedItem.item),
              'is-pinnable': isPinnedHandle(processedItem)
            }"
            role="button"
            :tabindex="getControlTabIndex(processedItem)"
            :aria-pressed="isItemGrabbed(processedItem.item) ? 'true' : 'false'"
            :aria-label="getHandleAriaLabel(processedItem)"
            @focus="handleHandleFocus(processedItem)"
            @blur="handleHandleBlur(processedItem)"
            @keydown="handleHandleKeydown($event, processedItem)"
            @focusout="handleHandleFocusout($event, processedItem)"
            @click.stop>
            <zm-icon class="side-menu-v3__item-icon">
              <component :is="getHandleIcon(processedItem)" />
            </zm-icon>
          </span>
          <zm-icon v-else class="side-menu-v3__item-icon">
            <component :is="getDisplayIcon(processedItem)" />
          </zm-icon>
        </template>

        <span role="presentation" class="side-menu-v3__item-label">
          <span class="side-menu-v3__item-label-text">{{ getItemLabel(processedItem) }}</span>
          <span
            v-if="
              !!getItemProp(processedItem, '_pinned') &&
              getItemProp(processedItem, 'pinnedLabelWithParent')
            "
            class="side-menu-v3__item-sub-label">
            {{ `(${getItemProp(processedItem, 'pinnedLabelWithParent')})` }}
          </span>
        </span>

        <zm-tooltip
          v-if="shouldShowPinButton(processedItem)"
          :content="getPinTooltip(processedItem)"
          :arrow="false"
          disallow-screen-reader
          :z-index="2002"
          placement="top">
          <zm-button
            type="text"
            size="sm"
            :tabindex="getControlTabIndex(processedItem)"
            :class="['side-menu-v3__item-pin-button', isItemPinned(processedItem) && 'is-pinned']"
            :icon="isItemPinned(processedItem) ? PinUnpin : Pin"
            :aria-label="getPinAriaLabel(processedItem)"
            @click.stop="handlePinClick($event, processedItem)"
            @keydown="handlePinButtonKeydown" />
        </zm-tooltip>

        <template v-if="getItemProp(processedItem, 'badge')?.type === 'custom'">
          <zm-badge
            class="side-menu-v3__item-badge"
            type="custom"
            :text="getItemProp(processedItem, 'badge').text"
            :color="getItemProp(processedItem, 'badge').color"
            :inverse="getItemProp(processedItem, 'badge').inverse" />
          <zm-visually-hidden :id="getDescriptionId(processedItem)">
            {{ getItemProp(processedItem, 'badge').text }}
          </zm-visually-hidden>
        </template>

        <zm-inline-chevron-icon
          v-if="isItemGroup(processedItem)"
          size="16px"
          :class="[
            'side-menu-v3__submenu-icon',
            processedItem.expanded && 'side-menu-v3__submenu-icon--expanded'
          ]" />

        <zm-icon
          v-else-if="getItemProp(processedItem, 'target') === '_blank'"
          class="side-menu-v3__item-external-icon">
          <pop-window-pop-out-v2 />
        </zm-icon>
      </component>

      <transition name="side-menu-v3-collapse" v-bind="collapseTransition">
        <side-menu-sub
          v-if="isItemGroup(processedItem)"
          v-show="processedItem.expanded"
          :is-pinned-menu="isPinnedMenu"
          :id="getItemId(processedItem) + '_list'"
          :menu-id="menuId"
          :class="['side-menu-v3__list', 'side-menu-v3__sub', `is-level-${level + 1}`]"
          :items="processedItem.items"
          :level="level + 1"
          :active-key="activeKey"
          :active-item="activeItem"
          :aria-label="getItemLabel(processedItem)"
          :pin-able="pinAble"
          :pinned-item-keys="pinnedItemKeys"
          :suppress-group-hover="suppressGroupHover"
          :hover-all-groups="hoverAllGroups"
          @item-click="$emit('item-click', $event)"
          @item-keyboard="$emit('item-keyboard', $event)"
          @item-pin="$emit('item-pin', $event)"
          @item-hover="$emit('item-hover', $event)"
          @item-hover-leave="$emit('item-hover-leave', $event)"
          @item-expanded-change="$emit('item-expanded-change', $event)" />
      </transition>
    </li>
  </ul>
</template>

<script setup>
import {
  ZmIcon,
  ZmBadge,
  ZmVisuallyHidden,
  ZmInlineChevronIcon,
  ZmTooltip,
  ZmButton,
  createContextMenu
} from '@zoom/zoom-ui-vue3'
import { Pin, PinUnpin, PopWindowPopOutV2, DragHandleDots, Checkmark } from '@zoom/icons-vue'
import { useSortable } from '@vueuse/integrations/useSortable'
import { watch, ref, toRef, computed } from 'vue'
import { isEmpty, resolve, collapseTransition } from './composables/use-tree'
import { trackEvent } from '@/shared/event-tracker'
import i18n from '../../../shared/i18n'

const { t } = i18n.global

defineOptions({ name: 'side-menu-v3-sub' })

const props = defineProps({
  isPinnedMenu: Boolean,
  menuId: String,
  level: { type: Number, default: 0 },
  items: Array,
  /** Current route / page key — drives `is-active` only; not updated by mere clicks */
  activeKey: { type: [String, Number], default: null },
  activeItem: Object,
  pinAble: { type: Boolean, default: false },
  pinnedItemKeys: { type: Set, default: () => new Set() },
  sortable: { type: Boolean, default: false },
  suppressGroupHover: { type: Boolean, default: false },
  /**
   * When true (used inside hover popups), emits item-hover for all group items
   * regardless of their expanded state.
   */
  hoverAllGroups: { type: Boolean, default: false },
  isInHoverPopup: { type: Boolean, default: false },
  /**
   * Predicate `(menuItem) => boolean` that returns true when the given pinned
   * menu item is currently in the keyboard "grabbed" state. Used to drive the
   * drag-handle icon swap and `aria-pressed` reflection without forcing this
   * sub-component to own grab state.
   */
  isItemGrabbed: { type: Function, default: () => false },
  /**
   * Label shown in the right-click context menu on a pinned draggable row.
   * Mirrors zm-dnd's "Move item" entry which lets mouse users start a drag
   * without holding the button down.
   */
  contextMoveLabel: { type: String, default: () => i18n.global.t('sideMenu.moveItem') }
})

const emit = defineEmits([
  'item-click',
  'item-keyboard',
  'item-pin',
  'item-hover',
  'item-hover-leave',
  'item-expanded-change',
  'sortable-reorder',
  'sort-start',
  'sort-end',
  'pinned-handle-keyboard',
  'pinned-handle-focusout',
  'pinned-context-move'
])

const sortableRootRef = ref(null)

// Sortable.js keeps drag state (tapEvt, ghostEl, Sortable.active, ...) on module-level globals,
// so destroying any inactive instance mid-drag wipes the active drag. Only enable on the
// pinned root when sortable is true — every other `<side-menu-sub>` (hover popups, nested
// groups, main list) or when sortable is false skips it.
if (props.sortable && props.level === 0) {
  useSortable(sortableRootRef, toRef(props, 'items'), {
    watchElement: true,
    animation: 180,
    forceFallback: true,
    fallbackOnBody: true,
    draggable: '.side-menu-v3__item.is-sortable-draggable',
    chosenClass: 'draggable-chosen',
    ghostClass: 'is-drag-placeholder-row',
    onStart: () => emit('sort-start'),
    onEnd: () => emit('sort-end'),
    // `oldDraggableIndex`/`newDraggableIndex` count only items matching `draggable`,
    // i.e. they skip the non-draggable "Pinned" title row and map 1:1 to `pinnedMenus`.
    onUpdate: (evt) => {
      const fromIndex = evt.oldDraggableIndex
      const toIndex = evt.newDraggableIndex
      if (fromIndex == null || toIndex == null || fromIndex === toIndex) return
      emit('sortable-reorder', { fromIndex, toIndex })
    }
  })
}

const isSameMenuItem = (a, b) => {
  if (!a || !b) return false
  const normalize = (v) => (v == null || v === '' ? '' : String(v))
  const aMenuKey = normalize(a?.item?.key ?? a?.item?.id)
  const bMenuKey = normalize(b?.item?.key ?? b?.item?.id)
  if (aMenuKey && bMenuKey && aMenuKey === bMenuKey) return true
  const aNodeKey = normalize(a?.key)
  const bNodeKey = normalize(b?.key)
  return !!(aNodeKey && bNodeKey && aNodeKey === bNodeKey)
}

const findFirstFocusableIndex = (items) =>
  (items ?? []).findIndex((item) => !isItemSeparator(item) && !isItemTitle(item))

const containsActiveInList = (items, active) => {
  for (const item of items ?? []) {
    if (isSameMenuItem(item, active)) return true
    if (item.items?.length && containsActiveInList(item.items, active)) return true
  }
  return false
}

// Row-level focus tracking: which row currently *contains* keyboard focus
// (treeitem content, drag handle, or pin button). Drives both:
//   1. Roving tabindex for the treeitem itself (so Shift+Tab from the inner
//      handle/pin button returns to the focused row's `<a>` instead of jumping
//      to a different anchor).
//   2. Inner controls (handle, pin button) only contribute a tab stop while
//      their row is focused, so users can Tab into a row's controls only
//      after navigating to that row (Tab in or ArrowUp/Down between treeitems).
const focusedRowKey = ref(null)

const isRowFocused = (processedItem) => focusedRowKey.value === processedItem.key

// Only react to focus events that target an element directly inside *this* row
// (its `<component>` content + any inner control like the drag handle / pin
// button). Focus changes inside a nested `<side-menu-sub>` also bubble through
// us, but they belong to that nested instance — claiming them here would
// incorrectly mark the parent row as focused and produce two tab anchors
// (the nested treeitem AND its ancestor group) and break Shift+Tab navigation.
const isFocusEventDirectlyInRow = (event) => event.target?.closest?.('li') === event.currentTarget

const handleRowFocusIn = (event, processedItem) => {
  if (!isFocusEventDirectlyInRow(event)) return
  focusedRowKey.value = processedItem.key
}

const handleRowFocusOut = (event, processedItem) => {
  if (focusedRowKey.value !== processedItem.key) return
  if (!isFocusEventDirectlyInRow(event)) return
  // Focus is moving within the same row's own content (e.g. content -> handle
  // -> pin): keep the row marked as focused. Note we compare by *closest li*
  // rather than `li.contains(relatedTarget)` — the latter also matches focus
  // moving down into a nested `<side-menu-sub>`, which would incorrectly keep
  // the parent row claimed and produce two tab anchors.
  const li = event.currentTarget
  const relatedLi = event.relatedTarget?.closest?.('li')
  if (relatedLi === li) return
  focusedRowKey.value = null
}

const getTabIndex = (processedItem, index) => {
  if (isItemSeparator(processedItem)) return undefined
  // Roving tabindex: once any row in this sub has been focused, only that
  // row's treeitem is the tab anchor. This matches the WAI-ARIA APG tree
  // pattern and keeps Tab/Shift+Tab between the treeitem and its inner
  // controls (handle, pin button) consistent.
  if (focusedRowKey.value != null) {
    return isRowFocused(processedItem) ? '0' : '-1'
  }
  // Initial state (no row focused yet): anchor on the active item, or fall
  // back to the first focusable row of the root list.
  if (isSameMenuItem(processedItem, props.activeItem)) return '0'
  if (props.level !== 0) return '-1'
  if (props.activeItem && containsActiveInList(props.items, props.activeItem)) return '-1'
  const firstFocusableIndex = findFirstFocusableIndex(props.items)
  return index === firstFocusableIndex ? '0' : '-1'
}

const getControlTabIndex = (processedItem) => (isRowFocused(processedItem) ? '0' : '-1')

const getItemId = (processedItem) => `${props.menuId}_${processedItem.key}`

const getItemProp = (processedItem, name, _params) => resolve(processedItem.item[name], _params)

const getItemLabel = (processedItem) => getItemProp(processedItem, 'label')

// For pinned items: show the item's own icon if available, otherwise fall back to parentIcon.
const getItemIcon = (processedItem) =>
  getItemProp(processedItem, 'icon') ||
  (getItemProp(processedItem, '_pinned') ? getItemProp(processedItem, 'parentIcon') : null)

const hoveredItemKey = ref('')

const isPinnedRootHovered = (processedItem) =>
  props.level === 0 &&
  props.sortable &&
  !!getItemProp(processedItem, '_pinned') &&
  hoveredItemKey.value === processedItem.key

const getDisplayIcon = (processedItem) =>
  isPinnedRootHovered(processedItem) ? DragHandleDots : getItemIcon(processedItem)

const isPinnedHandle = (processedItem) =>
  props.level === 0 && !!getItemProp(processedItem, '_pinned')

// Tracks the currently-keyboard-focused handle by its node key (stable across
// reorders). Used purely to drive the icon swap; grab state lives in `use-pinned`.
const focusedHandleKey = ref(null)

const isHandleFocused = (processedItem) => focusedHandleKey.value === processedItem.key

const handleHandleFocus = (processedItem) => {
  focusedHandleKey.value = processedItem.key
}

const handleHandleBlur = (processedItem) => {
  if (focusedHandleKey.value === processedItem.key) focusedHandleKey.value = null
}

// Icon precedence on the drag handle:
//   1. grabbed         → Checkmark      (signals "drop to confirm")
//   2. hovered/focused → DragHandleDots (signals "this is the drag handle")
//   3. otherwise       → item's own icon
const getHandleIcon = (processedItem) => {
  if (props.isItemGrabbed(processedItem.item)) return Checkmark
  if (props.sortable && (isPinnedRootHovered(processedItem) || isHandleFocused(processedItem))) {
    return DragHandleDots
  }
  return getItemIcon(processedItem)
}

const getHandleAriaLabel = (processedItem) => {
  const label = getItemLabel(processedItem) || t('aria.item')
  return props.isItemGrabbed(processedItem.item)
    ? t('aria.sideMenu.reorderGrabbed', [label])
    : t('aria.sideMenu.reorderIdle', [label])
}

const handleHandleKeydown = (event, processedItem) => {
  emit('pinned-handle-keyboard', { originalEvent: event, processedItem })
}

const handleHandleFocusout = (event, processedItem) => {
  emit('pinned-handle-focusout', { originalEvent: event, processedItem })
}

const getItemTitle = (processedItem) => getItemProp(processedItem, 'title')

const getItemAriaLabel = (processedItem) => {
  if (isItemTitle(processedItem)) {
    return getItemTitle(processedItem)
  }
  if (
    !!getItemProp(processedItem, '_pinned') &&
    getItemProp(processedItem, 'pinnedLabelWithParent')
  ) {
    return `${getItemLabel(processedItem)} (${getItemProp(processedItem, 'pinnedLabelWithParent')})`.trim()
  }
  return getItemLabel(processedItem)
}

const getDescriptionId = (processedItem) => getItemId(processedItem) + '__item'

const getDescribedById = (processedItem) => {
  const ids = []
  if (getItemProp(processedItem, 'badge')?.type === 'custom') {
    ids.push(getDescriptionId(processedItem))
  }
  if (hasInnerControls(processedItem)) {
    ids.push(getHintId(processedItem))
  }
  return ids.length ? ids.join(' ') : undefined
}

const HINT_HANDLE_PIN_TEXT = t('aria.sideMenu.hintHandlePin')
const HINT_HANDLE_TEXT = t('aria.sideMenu.hintHandle')
const HINT_PIN_TEXT = t('aria.sideMenu.hintPin')

// Stable shared hint IDs — one per variant, defined once per sub-list instance.
// All treeitems in this level share these nodes via aria-describedby so there
// is no per-row duplication of the hidden hint text.
const hintHandlePinId = computed(() => `${props.menuId}__hint_handle_pin`)
const hintHandleId = computed(() => `${props.menuId}__hint_handle`)
const hintPinId = computed(() => `${props.menuId}__hint_pin`)

// Returns true when the treeitem row has Tab-accessible inner controls
// (drag handle or pin button) that a screen reader user should be hinted about.
const hasInnerControls = (processedItem) =>
  shouldShowPinButton(processedItem) || isPinnedHandle(processedItem)

// True when at least one item at this level has inner controls, used to
// conditionally render the shared hint <li> once at the top of the list.
const anyItemHasInnerControls = computed(() => (props.items ?? []).some(hasInnerControls))

// Returns the appropriate shared hint ID for a given item's control set.
const getHintId = (processedItem) => {
  const hasHandle = isPinnedHandle(processedItem)
  const hasPin = shouldShowPinButton(processedItem)
  if (hasHandle && hasPin) return hintHandlePinId.value
  if (hasHandle) return hintHandleId.value
  return hintPinId.value
}

const isItemGroup = (processedItem) => !isEmpty(processedItem.items)

/**
 * Last known expanded per group key (deep mutation does not give reliable old/new in watch).
 * First time we see a key: emit only if expanded — covers activeKey initial expand and avoids
 * emitting once per collapsed branch on mount.
 */
const expandedSnapshot = ref(new Map())

watch(
  () => props.items,
  (items) => {
    if (!items?.length) return
    for (const processedItem of items) {
      if (!isItemGroup(processedItem)) continue
      const nextExpanded = !!processedItem.expanded
      const key = processedItem.key
      const prevExpanded = expandedSnapshot.value.get(key)
      let shouldEmit = false
      if (prevExpanded === undefined) {
        shouldEmit = nextExpanded
      } else if (prevExpanded !== nextExpanded) {
        shouldEmit = true
      }
      if (shouldEmit) {
        emit('item-expanded-change', { processedItem, expanded: nextExpanded })
      }
      expandedSnapshot.value.set(key, nextExpanded)
    }
  },
  { deep: true, immediate: true }
)

const isItemDisabled = (processedItem) =>
  getItemProp(processedItem, 'disabled') || isItemTitle(processedItem)

const isItemActive = (processedItem) => {
  if (isItemSeparator(processedItem) || isItemTitle(processedItem)) return false
  if (isItemGroup(processedItem)) return false
  const ak = props.activeKey
  if (ak == null || ak === '') return false
  const { key, id } = processedItem.item || {}
  const norm = (v) => (v == null || v === '' ? '' : String(v))
  const cmp = String(ak)
  return norm(key) === cmp || norm(id) === cmp
}
const isItemSeparator = (processedItem) => !!getItemProp(processedItem, 'separator')

const isItemTitle = (processedItem) =>
  !getItemProp(processedItem, 'url') && isEmpty(processedItem.items)

const isItemPinned = (processedItem) =>
  !!getItemProp(processedItem, '_pinned') ||
  props.pinnedItemKeys.has(processedItem.item?.key) ||
  props.pinnedItemKeys.has(processedItem.item?.id)

const shouldShowPinButton = (processedItem) => {
  if (isItemSeparator(processedItem) || isItemTitle(processedItem)) {
    return false
  }
  return (
    (props.pinAble && props.level > 0) ||
    (props.pinAble && props.isInHoverPopup) ||
    processedItem.item._pinned
  )
}

const getPinTooltip = (processedItem) =>
  isItemPinned(processedItem) ? t('sideMenu.unpin') : t('sideMenu.pin')

const getPinAriaLabel = (processedItem) =>
  isItemPinned(processedItem)
    ? t('aria.sideMenu.unpinItem', [getItemLabel(processedItem)])
    : t('aria.sideMenu.pinItem', [getItemLabel(processedItem)])

const handlePinClick = (event, processedItem) => {
  event.preventDefault()
  event.stopPropagation()
  emit('item-pin', { menuItem: processedItem.item })
}

// Pin button is a native <button>; Space / Enter on it natively fire `click`
// which we already handle in `handlePinClick`. However the keydown event ALSO
// bubbles to the parent `<a>/<div role="treeitem">` @keydown listener which
// emits `item-keyboard` and gets routed to the tree's group-toggle path. The
// net effect: pressing Space/Enter on the pin button fires the pin click AND
// collapses/expands the parent group at the same time.
// Stop propagation for those two keys only — leave default action intact so
// the native button still synthesises a click, and leave other keys (Tab,
// arrows, Esc, etc.) to bubble normally for focus traversal.
const handlePinButtonKeydown = (event) => {
  if (event.code === 'Space' || event.code === 'Enter') {
    event.stopPropagation()
  }
}

// Right-click → "Move" entry point for pinned rows. Mirrors zm-dnd's
// `createContextMenu` pattern (vue3-ui/src/components/dnd/dnd-item.vue) so
// mouse users who can't (or don't want to) drag still have an accessible way
// to start reordering. Selecting "Move" hands control to the same keyboard
// grab state machine used by the drag-handle's Space/Enter path: the parent
// receives `pinned-context-move`, enters grabbed mode and moves focus to the
// drag handle, where ↑/↓ reorders and Space/Enter or Esc releases.
//
// The handler is intentionally scoped to the sortable pinned root + concrete
// draggable rows. Titles, separators, disabled rows and any other
// `<side-menu-sub>` instance (hover popups, nested groups) fall through to
// the browser's native context menu.
//
// We pass the raw `Event` (not a Ref) to `createContextMenu`, which skips its
// own onMounted/onUnmounted listener registration — this avoids the
// "createContextMenu was called outside setup" foot-gun and means the helper
// is fully self-contained for a single open/close cycle.
const handleItemContextMenu = (event, processedItem) => {
  if (!props.sortable || props.level !== 0) return
  if (isItemTitle(processedItem) || isItemSeparator(processedItem)) return
  if (isItemDisabled(processedItem)) return

  const rowEl = event.currentTarget
  const helper = createContextMenu(event, {
    data: [
      {
        label: props.contextMoveLabel,
        command: () => emit('pinned-context-move', { processedItem, rowEl })
      }
    ]
  })
  helper.open()
  event.preventDefault()
  event.stopPropagation()
}

// Touch tracking for the touchend navigation fallback.
// Plain variables – no reactivity needed, just stable state within a touch gesture.
let _touchStartX = 0
let _touchStartY = 0

const handleItemTouchStart = (event) => {
  const touch = event.touches[0]
  if (!touch) return
  _touchStartX = touch.clientX
  _touchStartY = touch.clientY
}

// Second line of defence against iOS's two-tap behavior for <a href> elements:
// On the first tap iOS synthesizes mouseover → :hover activates → if any visual
// change occurs the click is suppressed until the second tap. The CSS
// `@media (hover: none)` reset handles this for styles in our component, but
// external/CDN rules (e.g. `.nav-menu a:hover`) can have higher specificity and
// still cause a visible change. As a fallback, we navigate programmatically on
// touchend for plain leaf-URL items (no custom `command` handler). This fires
// before iOS finishes its hover-on-tap decision, so navigation always happens on
// the first tap.
// Items that use a `command` callback for SPA navigation (e.g. QM/ZCC sub-apps)
// are deliberately excluded so their command runs via the synthesized click.
const handleItemTouchEnd = (event, processedItem) => {
  const touch = event.changedTouches[0]
  if (!touch) return
  // Ignore scroll/swipe gestures (finger moved > 10 px)
  if (Math.abs(touch.clientX - _touchStartX) > 10 || Math.abs(touch.clientY - _touchStartY) > 10)
    return

  const url = getItemProp(processedItem, 'url')
  if (!url) return
  if (isItemGroup(processedItem)) return
  if (isItemDisabled(processedItem) || isItemSeparator(processedItem)) return
  // Items with a command callback handle their own navigation (SPA router.push etc.)
  if (processedItem.item?.command) return

  // Track click before navigating
  trackMenuClick(processedItem)
  emit('item-click', { originalEvent: event, processedItem })

  const target = getItemProp(processedItem, 'target')
  if (target === '_blank') {
    window.open(url, '_blank')
  } else {
    window.location.href = url
  }
}

const handleItemMouseenter = (event, processedItem) => {
  // On touch devices the browser synthesizes pointerenter/mouseenter as part of
  // the tap gesture. Updating hover state in response causes iOS/Android to treat
  // the first tap as "applying hover" and suppress the click, requiring a second
  // tap. Skip all hover logic for touch interactions.
  if (event.pointerType === 'touch') return
  hoveredItemKey.value = processedItem.key
  if (props.suppressGroupHover) return
  if (!isItemGroup(processedItem)) return
  if (!props.hoverAllGroups && processedItem.expanded) return
  emit('item-hover', { triggerEl: event.currentTarget, processedItem })
}

const handleItemMouseleave = (event, processedItem) => {
  if (event.pointerType === 'touch') return
  if (hoveredItemKey.value === processedItem.key) hoveredItemKey.value = ''
  if (!isItemGroup(processedItem)) return
  emit('item-hover-leave', { triggerEl: event.currentTarget, processedItem, originalEvent: event })
}

const trackMenuClick = (processedItem) => {
  const menuItem = processedItem?.item
  const label = menuItem?.labelEn ?? menuItem?.label ?? ''
  trackEvent({
    linkText: label.toLowerCase(),
    linkUrl: menuItem?.url || 'undefined',
    uiLocation: 'web portal navigation - left-hand desktop menu'
  })
}

const handleItemClick = (event, processedItem) => {
  if (isItemDisabled(processedItem) || isItemSeparator(processedItem)) {
    // `aria-disabled` does NOT block native navigation on `<a href>`.
    // Suppress the default action so disabled items truly behave as disabled.
    event.preventDefault()
    return
  }
  // Group rows (`<a href>` with sub-items) must expand/collapse on click,
  // not navigate. Suppress the native link behaviour so mouse clicks agree
  // with keyboard Space/Enter (both routed through the parent's
  // `handleItemKeydown` → `toggleTreeExpansion` / `togglePinnedExpansion`).
  if (isItemGroup(processedItem)) {
    event.preventDefault()
  }
  trackMenuClick(processedItem)
  // Hover flyout: opening deeper levels is hover-only; click on a group should do nothing
  // (no command, no stack/sidebar side effects — leaf clicks still emit).
  if (props.isInHoverPopup && isItemGroup(processedItem)) return
  getItemProp(processedItem, 'command', { originalEvent: event, item: processedItem.item })
  emit('item-click', { originalEvent: event, processedItem })
}

const handleKeydown = (event, processedItem) => {
  emit('item-keyboard', { originalEvent: event, processedItem })
}
</script>
